import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiPlus, FiTrash2, FiTag, FiCheck, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { API_URL } from '../config';
import BmpFigurePicker from './BmpFigurePicker';

/**
 * The BMP codes offered by the photo markup tool.
 *
 * Kept as editable data rather than hard-coded: the codes and their wording come
 * from the GSWCC manual, so the office owns them and can correct or extend the
 * list without a code change. Seeded codes ship with blank descriptions on
 * purpose — nobody should be guessing regulatory wording on a sealed letter.
 */
function BmpLibrary() {
  const [symbols, setSymbols] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newCode, setNewCode] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [loading, setLoading] = useState(true);
  const [savedId, setSavedId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [figureFor, setFigureFor] = useState(null);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/bmp-symbols?all=1`, config);
      setSymbols(res.data || []);
    } catch (err) {
      console.error('Could not load BMP symbols', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (sym, body) => {
    try {
      await axios.patch(`${API_URL}/bmp-symbols/${sym.id}`, body, config);
      setSavedId(sym.id);
      setTimeout(() => setSavedId(null), 1600);
      await load();
    } catch (err) {
      alert('Could not save: ' + (err.response?.data?.error || err.message));
    }
  };

  const FIELDS = ['code', 'description', 'description_long', 'notes'];

  const saveRow = (sym) => {
    const d = drafts[sym.id];
    if (!d) return;
    const changed = FIELDS.some(f => d[f] !== undefined && d[f] !== (sym[f] || ''));
    if (!changed) return;
    patch(sym, FIELDS.reduce((acc, f) => {
      acc[f] = d[f] !== undefined ? d[f] : (sym[f] || '');
      return acc;
    }, {}));
  };

  const addSymbol = async () => {
    const code = newCode.trim();
    if (!code) return;
    try {
      await axios.post(`${API_URL}/bmp-symbols`, { code, description: newDesc.trim() }, config);
      setNewCode('');
      setNewDesc('');
      await load();
    } catch (err) {
      alert('Could not add: ' + (err.response?.data?.error || err.message));
    }
  };

  const removeSymbol = async (sym) => {
    if (!window.confirm(`Remove "${sym.code}" from the BMP list?\n\nPhotos already marked with it keep their markup.`)) return;
    try {
      await axios.delete(`${API_URL}/bmp-symbols/${sym.id}`, config);
      await load();
    } catch (err) {
      alert('Could not remove: ' + (err.response?.data?.error || err.message));
    }
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', textTransform: 'uppercase',
    color: '#888', fontWeight: 600, marginBottom: '4px', letterSpacing: '.03em',
  };
  const inputStyle = {
    width: '100%', padding: '9px 11px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
  };

  const missingDescriptions = symbols.filter(s => !s.description).length;

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '16px', padding: '20px',
      marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FiTag size={18} /> BMP Codes
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#999', lineHeight: 1.5 }}>
        Erosion &amp; sediment control codes you can place on photos. Expand a code to
        read its practice description and add the instruction your contractor needs &mdash;
        both print on the BMP details page of any letter that uses it.
      </p>

      {missingDescriptions > 0 && (
        <div style={{
          padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: '8px', fontSize: '13px', color: '#92400e', marginBottom: '14px',
        }}>
          {missingDescriptions} code{missingDescriptions === 1 ? ' has' : 's have'} no description yet.
        </div>
      )}

      {loading ? (
        <div style={{ color: '#aaa', fontSize: '13px' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {symbols.map(sym => {
            const d = drafts[sym.id] || {};
            return (
              <React.Fragment key={sym.id}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  style={{ ...inputStyle, flex: '0 0 116px', fontWeight: 700 }}
                  value={d.code !== undefined ? d.code : sym.code}
                  onChange={(e) => setDrafts(p => ({ ...p, [sym.id]: { ...d, code: e.target.value } }))}
                  onBlur={() => saveRow(sym)}
                />
                <input
                  style={inputStyle}
                  placeholder="Description (from the GSWCC manual)"
                  value={d.description !== undefined ? d.description : (sym.description || '')}
                  onChange={(e) => setDrafts(p => ({ ...p, [sym.id]: { ...d, description: e.target.value } }))}
                  onBlur={() => saveRow(sym)}
                />
                {savedId === sym.id && <FiCheck size={16} color="#16a34a" />}
                <button
                  type="button"
                  onClick={() => setOpenId(openId === sym.id ? null : sym.id)}
                  title="Practice description and site instructions"
                  style={{
                    padding: '9px 10px',
                    background: sym.notes ? '#0f2942' : 'white',
                    color: sym.notes ? 'white' : '#374151',
                    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
                    lineHeight: 0,
                  }}
                >
                  {openId === sym.id ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => removeSymbol(sym)}
                  title="Remove code"
                  style={{
                    padding: '9px 10px', background: 'white', color: '#d33',
                    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', lineHeight: 0,
                  }}
                >
                  <FiTrash2 size={14} />
                </button>
                </div>
                {openId === sym.id && (
                  <div style={{
                    margin: '2px 0 10px', padding: '12px 14px', background: '#fafafa',
                    border: '1px solid #eee', borderRadius: '10px',
                  }}>
                    <label style={labelStyle}>Practice description</label>
                    <div style={{ fontSize: '12px', color: '#999', margin: '0 0 5px' }}>
                      From the GSWCC manual. Prints on the BMP details page of any letter using this code.
                    </div>
                    <textarea
                      rows={4}
                      style={{ ...inputStyle, fontSize: '13px', resize: 'vertical', fontFamily: 'inherit' }}
                      value={d.description_long !== undefined ? d.description_long : (sym.description_long || '')}
                      onChange={(e) => setDrafts(pr => ({ ...pr, [sym.id]: { ...d, description_long: e.target.value } }))}
                      onBlur={() => saveRow(sym)}
                    />
                    <label style={{ ...labelStyle, marginTop: '12px' }}>Required on this project</label>
                    <div style={{ fontSize: '12px', color: '#999', margin: '0 0 5px' }}>
                      Your standing instruction to the contractor or owner &mdash; spacing, depth,
                      inspection, maintenance. Written once, printed every time this BMP is used.
                    </div>
                    <textarea
                      rows={3}
                      placeholder="e.g. Support spacing no greater than 6 feet on center, driven a minimum of 18 inches. Inspect after every rainfall."
                      style={{ ...inputStyle, fontSize: '13px', resize: 'vertical', fontFamily: 'inherit' }}
                      value={d.notes !== undefined ? d.notes : (sym.notes || '')}
                      onChange={(e) => setDrafts(pr => ({ ...pr, [sym.id]: { ...d, notes: e.target.value } }))}
                      onBlur={() => saveRow(sym)}
                    />

                    <label style={{ ...labelStyle, marginTop: '12px' }}>Construction details</label>
                    <div style={{ fontSize: '12px', color: '#999', margin: '0 0 6px' }}>
                      GSWCC drawings to print with this practice, so the contractor sees how it is built.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={() => setFigureFor(sym)}
                        style={{
                          padding: '8px 14px', background: 'white', color: '#374151',
                          border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: 600,
                        }}
                      >
                        {sym.figure_keys?.length ? 'Change details' : 'Attach details'}
                      </button>
                      <span style={{ fontSize: '13px', color: sym.figure_keys?.length ? '#166534' : '#aaa' }}>
                        {sym.figure_keys?.length
                          ? `${sym.figure_keys.length} drawing${sym.figure_keys.length === 1 ? '' : 's'} attached`
                          : 'none attached'}
                      </span>
                    </div>
                  </div>
                )}
                </React.Fragment>
            );
          })}
        </div>
      )}

      {figureFor && (
        <BmpFigurePicker
          symbol={figureFor}
          onSave={() => { setFigureFor(null); load(); }}
          onClose={() => setFigureFor(null)}
        />
      )}

      <div style={{ borderTop: '1px solid #eee', paddingTop: '14px' }}>
        <label style={labelStyle}>Add a code</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...inputStyle, flex: '0 0 116px', fontWeight: 700 }}
            placeholder="Code"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addSymbol(); }}
          />
          <input
            style={inputStyle}
            placeholder="Description"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addSymbol(); }}
          />
          <button
            type="button"
            onClick={addSymbol}
            disabled={!newCode.trim()}
            style={{
              padding: '9px 16px', background: newCode.trim() ? '#0f2942' : '#e5e7eb',
              color: newCode.trim() ? 'white' : '#9ca3af', border: 'none', borderRadius: '8px',
              cursor: newCode.trim() ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
            }}
          >
            <FiPlus size={14} /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default BmpLibrary;
